import { ChangeEvent, ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { styled, TextField, TextFieldProps } from '@mui/material'
import { last, times } from 'lodash'

import { HTMLTextFieldElement } from 'common/interfaces'

const hiddenInputStyleObject = {
  height: 0,
  padding: 0,
  border: 'none',
  outline: 'none',
}

const InputSegmentsWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
})
const CodeInputSegment = styled(TextField)(({ theme }) => ({
  // One time password input consists of 6 square inputs (code input segments)
  // And looks like this: □ □ □ □ □ □
  // So 100% / 6  is the width of one segment and 2% (or 1% for mobile) is the gap between segments
  width: 'calc(100% / 6 - 2%)',

  [theme.breakpoints.down('sm')]: {
    width: 'calc(100% / 6 - 1%)',
  },
}))

const HiddenInput = styled(TextField)({
  '& fieldset': hiddenInputStyleObject,

  '& p': {
    marginLeft: 0,
    textAlign: 'center',
  },
})

export default function VerificationCodeInput(props: TextFieldProps): JSX.Element {
  const [code, setCode] = useState(times(6, () => ''))
  const [focus, setFocus] = useState(0)

  const inputs = useRef<HTMLInputElement[]>([])
  const hiddenInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (focus !== -1) {
      inputs.current[focus].focus()
    }
  }, [focus])

  useEffect(() => {
    const codeString = code.join('')
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    )!.set
    nativeInputValueSetter?.call(hiddenInput.current, codeString)

    const inputEvent = new Event('input', { bubbles: true })
    hiddenInput.current?.dispatchEvent(inputEvent)
  }, [code])

  const handleCodeChange = (e: ChangeEvent<HTMLTextFieldElement>, index: number): void => {
    let value = e.target.value
    if (isNaN(parseInt(value))) {
      return
    }

    if (value.length > 1) {
      value = last(value)!
    }

    setCode((prevCode) => {
      const newCode = [...prevCode]
      newCode[index] = value
      return newCode
    })

    value.length > 0 ? index < 5 && setFocus(index + 1) : index > 0 && setFocus(index - 1)
  }

  const handleCodePaste = (e: ClipboardEvent<HTMLTextFieldElement>): void => {
    e.preventDefault()
    const paste = (e.clipboardData ?? window.Clipboard).getData('text')
    if (isNaN(parseInt(paste)) || paste.length !== 6) {
      return
    }

    const newCode = paste.split('')
    setCode(newCode)
  }

  const handleCodeKeyDown = (e: KeyboardEvent<HTMLTextFieldElement>, index: number): void => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      const newCode = [...code]
      newCode[index] = ''
      setCode(newCode)
      if (index > 0) {
        setFocus(index - 1)
      }
    }
  }

  const handleCodeKeyUp = (e: KeyboardEvent, index: number): void => {
    if (e.key === 'ArrowLeft') {
      if (index > 0) {
        setFocus(index - 1)
      }
    } else if (e.key === 'ArrowRight') {
      if (index < 5) {
        setFocus(index + 1)
      }
    }
  }

  return (
    <>
      <InputSegmentsWrapper>
        {code.map((digit, index) => (
          <CodeInputSegment
            inputRef={(el) => (inputs.current[index] = el as HTMLInputElement)}
            key={index}
            type="text"
            inputProps={{
              onFocus: () => setFocus(index),
              onKeyDown: (e) => handleCodeKeyDown(e, index),
              onKeyUp: (e) => handleCodeKeyUp(e, index),
              onPaste: handleCodePaste,
              onClick: () => setFocus(index),
              // added this style here because it didn't work in styled component styles
              style: { textAlign: 'center' },
              form: {
                autoComplete: 'no',
              },
            }}
            value={digit}
            onChange={(e) => handleCodeChange(e, index)}
          />
        ))}
      </InputSegmentsWrapper>
      <HiddenInput
        inputRef={hiddenInput}
        inputProps={{
          style: hiddenInputStyleObject,
        }}
        {...props}
      />
    </>
  )
}
